import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  FileText, Download, Loader2, Sparkles, MessageCircle, AlertCircle, 
  Trophy, Star, TrendingUp, MapPin, Phone, ChevronRight, X, Award, Users, ExternalLink 
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import BookingModal from "@/components/BookingModal";
import "../CutoffExplorer.css";

export default function DownloadCutoffs() {
  const navigate = useNavigate();
  const [pdfs, setPdfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showHowWeWorkModal, setShowHowWeWorkModal] = useState(false);

  useEffect(() => {
    const fetchPdfs = async () => {
      try {
        const res = await api.get("/public/cutoff-pdfs");
        const streamOrder = ["MBBS", "BDS", "BAMS", "BHMS", "BPTH"];
        const sorted = (res.data || []).sort((a, b) => {
          const getRank = (name) => {
            const upper = name.toUpperCase();
            const idx = streamOrder.findIndex(s => upper.includes(s));
            return idx !== -1 ? idx : 999;
          };
          return getRank(a.name) - getRank(b.name);
        });
        setPdfs(sorted);
      } catch (err) {
        console.error("Failed to fetch cutoff PDFs", err);
        setError("Unable to load downloads list. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchPdfs();
  }, []);

  const handleDownload = (filename) => {
    // Navigate window directly to the download endpoint to trigger browser file download
    window.open(`${api.defaults.baseURL || ""}/public/cutoff-pdfs/download?filename=${encodeURIComponent(filename)}`, "_blank");
    toast.success(`Downloading ${filename}...`);
  };

  const handleOrderHandbook = () => {
    const msg = "Hello Admissions Made Easy, I would like to order the physical copy of the NEET UG 2026 Admission Process Handbook. Please share the details.";
    window.open(`https://wa.me/919970809003?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#020617" }}>
      
      {/* ── TICKER STRIP ────────────────────────────────────────── */}
      <div className="ce-ticker-strip">
        <div className="ce-ticker-track">
          <span className="ce-ticker-item">🏆 861+ MBBS Students Successfully Allotted in NEET 2026 (Round 1, Round 2 &amp; Round 3 — Still Counting!)</span>
          <span className="ce-ticker-sep" />
          <span className="ce-ticker-item">📍 Branch Offices: Latur · Pune · Solapur · Kolhapur · Nanded</span>
          <span className="ce-ticker-sep" />
          <span className="ce-ticker-item">🏛️ Since 2011 — 14+ Years of Expert Medical Admission Guidance</span>
          <span className="ce-ticker-sep" />
          <span className="ce-ticker-item">🎯 Govt &amp; Private Colleges — Maharashtra, Karnataka &amp; All India</span>
          <span className="ce-ticker-sep" />
          <span className="ce-ticker-item">✅ SML-Based Precision Preference Filling — No Seat Left on Table</span>
          <span className="ce-ticker-sep" />
          <span className="ce-ticker-item">🏆 861+ MBBS Students Successfully Allotted in NEET 2026 (Round 1, Round 2 &amp; Round 3 — Still Counting!)</span>
          <span className="ce-ticker-sep" />
          <span className="ce-ticker-item">📍 Branch Offices: Latur · Pune · Solapur · Kolhapur · Nanded</span>
          <span className="ce-ticker-sep" />
          <span className="ce-ticker-item">🏛️ Since 2011 — 14+ Years of Expert Medical Admission Guidance</span>
        </div>
      </div>

      {/* ── SLEEK RESPONSIVE BRAND BAR ───────────────────────────── */}
      <div style={{
        background: "rgba(15, 23, 42, 0.75)",
        backdropFilter: "blur(12px)",
        borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
        padding: "12px 24px"
      }}>
        <div style={{
          maxWidth: 1200,
          margin: "0 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 14
        }}>
          {/* Brand info */}
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <img 
              src="/landing-assets/images/branding/logo1.png" 
              alt="Admissions Made Easy" 
              style={{ height: 34, width: "auto", objectFit: "contain", borderRadius: 4 }} 
            />
            <div>
              <div style={{ fontSize: "0.9rem", fontWeight: 800, color: "#f8fafc", lineHeight: 1.2 }}>
                Admissions Made Easy
              </div>
              <div style={{ fontSize: "0.68rem", color: "#94a3b8", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <span>⭐ Trusted Since 2011 · 14+ Years</span>
                <span style={{ color: "#475569" }}>•</span>
                <span style={{ color: "#fbbf24", fontWeight: 600 }}>📍 Latur · Pune · Solapur · Kolhapur · Nanded</span>
              </div>
            </div>
          </div>

          {/* Center Milestone Pill */}
          <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            padding: "6px 14px",
            borderRadius: 24,
            background: "linear-gradient(135deg, rgba(251, 191, 36, 0.12), rgba(245, 158, 11, 0.05))",
            border: "1px solid rgba(251, 191, 36, 0.3)"
          }}>
            <Trophy size={15} style={{ color: "#fbbf24", flexShrink: 0 }} />
            <div>
              <span style={{ fontSize: "0.88rem", fontWeight: 900, color: "#fbbf24" }}>861+</span>
              <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#e2e8f0", marginLeft: 6 }}>MBBS Admissions Secured (NEET 2026)</span>
            </div>
          </div>

          {/* Right Action buttons */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <button 
              className="ce-btn-counselling"
              style={{ padding: "0.45rem 0.9rem", fontSize: "0.76rem" }}
              onClick={() => setShowBookingModal(true)}
            >
              <Phone size={13} /> Book Free Counselling
            </button>
            <button 
              className="ce-back-btn"
              style={{ padding: "0.45rem 0.85rem", fontSize: "0.76rem" }}
              onClick={() => setShowHowWeWorkModal(true)}
            >
              <ChevronRight size={13} /> How AME Works
            </button>
            <a 
              href="tel:+919309553235" 
              className="ce-btn-phone"
              style={{ padding: "0.45rem 0.75rem", fontSize: "0.74rem" }}
            >
              <Phone size={12} /> 9309553235
            </a>
          </div>
        </div>
      </div>

      <div style={{ padding: "2.5rem 1.5rem 4rem", maxWidth: 1100, margin: "0 auto", color: "#f8fafc" }}>
        
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "3rem" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 16px", borderRadius: 30, background: "rgba(168,85,247,0.1)", border: "1px solid rgba(168,85,247,0.25)", marginBottom: 16 }}>
            <Sparkles size={14} style={{ color: "#a855f7" }} />
            <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#a855f7", letterSpacing: "1px", textTransform: "uppercase" }}>Official Resources</span>
          </div>
          <h1 style={{ fontSize: "2rem", fontWeight: 800, margin: "0 0 10px", background: "linear-gradient(to right, #f8fafc, #cbd5e1)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            Download Previous Year Cutoff Data
          </h1>
          <p style={{ color: "#94a3b8", fontSize: "0.95rem", maxWidth: 600, margin: "0 auto", lineHeight: 1.5 }}>
            Access and download official category-wise cutoffs for various medical streams including MBBS, BDS, BAMS, BHMS, and BPTH.
          </p>
        </div>

        {/* Interactive Cutoff Explorer Callout */}
        <div style={{
          marginBottom: "2rem",
          padding: "16px 20px",
          borderRadius: 16,
          background: "linear-gradient(135deg, rgba(6, 182, 212, 0.12), rgba(2, 132, 199, 0.06))",
          border: "1px solid rgba(6, 182, 212, 0.35)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 14,
          boxShadow: "0 4px 20px rgba(6, 182, 212, 0.1)"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ 
              width: 42, 
              height: 42, 
              borderRadius: 10, 
              background: "rgba(6, 182, 212, 0.15)", 
              border: "1px solid rgba(6, 182, 212, 0.3)", 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center",
              flexShrink: 0
            }}>
              <TrendingUp size={22} style={{ color: "#38bdf8" }} />
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: "0.95rem", fontWeight: 800, color: "#f8fafc" }}>
                  Interactive NEET 2026 Cutoff Explorer
                </span>
                <span style={{ 
                  fontSize: "0.62rem", 
                  fontWeight: 800, 
                  padding: "2px 8px", 
                  borderRadius: 12, 
                  background: "linear-gradient(135deg, #10b981, #059669)", 
                  color: "#ffffff" 
                }}>
                  Round 3 LATEST Live
                </span>
              </div>
              <p style={{ margin: "2px 0 0", fontSize: "0.78rem", color: "#94a3b8" }}>
                Explore live college-wise &amp; category-wise AIR, Marks, SML and Quotas across Round 1, Round 2 &amp; Round 3.
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate("/neet-2026-cutoff")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 18px",
              borderRadius: 10,
              background: "linear-gradient(135deg, #06b6d4, #0284c7)",
              color: "#ffffff",
              fontWeight: 800,
              fontSize: "0.82rem",
              border: "none",
              cursor: "pointer",
              boxShadow: "0 0 16px rgba(6, 182, 212, 0.4)",
              transition: "transform 0.15s ease"
            }}
            onMouseEnter={e => e.currentTarget.style.transform = "translateY(-1px)"}
            onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}
          >
            Open Cutoff Explorer <ExternalLink size={14} />
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 30, alignItems: "start" }}>
          
          {/* Left Side: PDF List */}
          <div style={{ background: "rgba(15,23,42,0.3)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 20, padding: 24 }}>
            <h2 style={{ fontSize: "1.15rem", fontWeight: 700, marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
              <FileText size={18} style={{ color: "#a855f7" }} />
              Available PDF Downloads
            </h2>

            {loading ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyCenter: "center", padding: "40px 0", gap: 12 }}>
                <Loader2 className="animate-spin" size={24} style={{ color: "#a855f7" }} />
                <span style={{ fontSize: "0.85rem", color: "#64748b" }}>Loading cutoff files...</span>
              </div>
            ) : error ? (
              <div style={{ display: "flex", alignItems: "center", gap: 10, background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 12, padding: 16, color: "#fca5a5", fontSize: "0.85rem" }}>
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            ) : pdfs.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px 0", color: "#64748b", fontSize: "0.88rem" }}>
                No cutoff PDFs available at the moment.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {pdfs.map((pdf, idx) => (
                  <div 
                    key={idx}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "12px 16px",
                      background: "rgba(255,255,255,0.02)",
                      border: "1px solid rgba(255,255,255,0.05)",
                      borderRadius: 12,
                      transition: "all 0.15s"
                    }}
                    className="pdf-item-row"
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                      <div style={{ background: "rgba(239,68,68,0.1)", color: "#ef4444", padding: 8, borderRadius: 8 }}>
                        <FileText size={18} />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "#e2e8f0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={pdf.name}>
                          {pdf.name.replace(".pdf", "")}
                        </div>
                        <div style={{ fontSize: "0.68rem", color: "#64748b", marginTop: 2 }}>
                          PDF Document · {pdf.size_mb} MB
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDownload(pdf.name)}
                      style={{
                        background: "rgba(168,85,247,0.1)",
                        border: "1px solid rgba(168,85,247,0.2)",
                        color: "#c084fc",
                        padding: "6px 12px",
                        borderRadius: 8,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        transition: "all 0.15s"
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.background = "rgba(168,85,247,0.2)";
                        e.currentTarget.style.color = "#d8b4fe";
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = "rgba(168,85,247,0.1)";
                        e.currentTarget.style.color = "#c084fc";
                      }}
                    >
                      <Download size={12} />
                      Download
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Side: Handbook Order Card */}
          <div style={{ background: "rgba(15,23,42,0.3)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 20, padding: 24 }}>
            <h2 style={{ fontSize: "1.15rem", fontWeight: 700, marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
              <Sparkles size={18} style={{ color: "#f59e0b" }} />
              Physical Copy Handbook
            </h2>

            <div style={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 16, overflow: "hidden" }}>
              
              {/* Actual Book Cover Image */}
              <div style={{ width: "100%", height: "auto", overflow: "hidden", display: "flex", justifyContent: "center", background: "#f8fafc", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                <img 
                  src="/landing-assets/images/handbooks/med-cover.jpeg" 
                  alt="NEET UG 2026 Handbook Cover" 
                  style={{ width: "100%", height: "auto", display: "block", objectFit: "contain" }} 
                />
              </div>

              {/* Book Info & Description */}
              <div style={{ padding: 20 }}>
                <div style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "1px", color: "#64748b", fontWeight: 700, marginBottom: 4 }}>
                  Authors
                </div>
                <div style={{ fontSize: "0.85rem", fontWeight: 700, color: "#f8fafc", marginBottom: 2 }}>
                  Sachin Bangad · Kailash Toshniwal
                </div>
                <div style={{ fontSize: "0.72rem", color: "#64748b", marginBottom: 16 }}>
                  Admissions Made Easy — Since 2011
                </div>

                {/* Tags */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 20 }}>
                  {["AIIMS", "AFMC", "AIQ", "STATE QUOTA", "DEEMED", "OTHER STATE PRIVATE MBBS / BAMS"].map((tag, idx) => (
                    <span 
                      key={idx}
                      style={{
                        fontSize: "0.62rem",
                        fontWeight: 700,
                        padding: "3px 8px",
                        borderRadius: 20,
                        background: "rgba(255,255,255,0.03)",
                        border: "1px solid rgba(255,255,255,0.06)",
                        color: "#fbbf24"
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Order Button */}
                <button
                  onClick={handleOrderHandbook}
                  style={{
                    width: "100%",
                    background: "linear-gradient(135deg, #f59e0b, #d97706)",
                    border: "none",
                    color: "#ffffff",
                    padding: "12px 20px",
                    borderRadius: 12,
                    fontSize: "0.82rem",
                    fontWeight: 700,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    boxShadow: "0 4px 12px rgba(245,158,11,0.2)",
                    transition: "all 0.15s"
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow = "0 6px 16px rgba(245,158,11,0.3)";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(245,158,11,0.2)";
                  }}
                >
                  <MessageCircle size={16} fill="currentColor" />
                  Order Now via WhatsApp
                </button>
              </div>

            </div>
          </div>

        </div>

      </div>

      {/* Booking Session Modal */}
      {showBookingModal && (
        <BookingModal
          isOpen={showBookingModal}
          onClose={() => setShowBookingModal(false)}
        />
      )}

      {/* ── HOW AME WORKS MODAL ──────────────── */}
      {showHowWeWorkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm" style={{ animation: 'fadeIn 0.2s ease' }}>
          <div className="ame-how-modal">
            <button
              onClick={() => setShowHowWeWorkModal(false)}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white rounded-full bg-slate-800/80 transition-colors"
            >
              <X size={18} />
            </button>

            <div className="ame-how-header">
              <div className="ame-how-logo-wrap">
                <img src="/landing-assets/images/branding/logo1.png" alt="AME" className="h-10 w-auto object-contain" />
              </div>
              <div>
                <h2 className="ame-how-title">How Admissions Made Easy Works</h2>
                <p className="ame-how-subtitle">Our proven 4-step framework that secured 861+ MBBS seats in NEET 2026</p>
              </div>
            </div>

            <div className="ame-how-steps">
              <div className="ame-how-step">
                <div className="ame-step-num" style={{ background: 'linear-gradient(135deg, #f39c12, #e67e22)' }}>1</div>
                <div className="ame-step-body">
                  <div className="ame-step-title">📊 Score &amp; SML Rank Evaluation</div>
                  <div className="ame-step-desc">We analyze your NEET score against the actual Maharashtra State Merit List (SML) trends for 58+ quota categories — giving you a precise picture of which government and private colleges are within your reach.</div>
                </div>
              </div>
              <div className="ame-how-step">
                <div className="ame-step-num" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>2</div>
                <div className="ame-step-body">
                  <div className="ame-step-title">🎯 Custom Priority List Creation</div>
                  <div className="ame-step-desc">Hand-crafted, error-free college preference lists tailored to your budget, location preference, bond terms, and seat security — ensuring maximum upgrade potential without risking your allotment.</div>
                </div>
              </div>
              <div className="ame-how-step">
                <div className="ame-step-num" style={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)' }}>3</div>
                <div className="ame-step-body">
                  <div className="ame-step-title">📝 Document Verification &amp; Form Filling</div>
                  <div className="ame-step-desc">Step-by-step guidance through form filling, document submission, and reporting to avoid common errors that lead to rejection at document scrutiny in government medical colleges.</div>
                </div>
              </div>
              <div className="ame-how-step">
                <div className="ame-step-num" style={{ background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)' }}>4</div>
                <div className="ame-step-body">
                  <div className="ame-step-title">🔄 Round 3 &amp; Upgrade Advisory</div>
                  <div className="ame-step-desc">After Round 2 allotment, we guide you on whether to retain your current seat or safely participate in Round 3 / Stray Vacancy rounds for a better college — without forfeiting security deposits.</div>
                </div>
              </div>
            </div>

            <div className="ame-how-footer">
              <div className="ame-how-trust-badges">
                <div className="ame-trust-badge"><Trophy size={13} /> 861+ MBBS Allotted (2026)</div>
                <div className="ame-trust-badge"><Award size={13} /> Since 2011</div>
                <div className="ame-trust-badge"><MapPin size={13} /> Offices: Latur, Pune, Solapur, Kolhapur, Nanded</div>
                <div className="ame-trust-badge"><Users size={13} /> 1-on-1 Dedicated Counsellor</div>
              </div>
              <button
                className="ame-cta-primary w-full mt-4"
                onClick={() => { setShowHowWeWorkModal(false); setShowBookingModal(true); }}
              >
                <Phone size={15} /> Book Your Free Counselling Session Now
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
